import { showToast, ToastSeverity } from './notifications';

/**
 * Interface matching the AresLegalEntityInfo DTO from backend
 */
interface AresLegalEntityInfo {
  CompanyIdentificationNumber: string;
  CompanyName: string;
  CompanyTaxIdentificationNumber: string;
  Street?: string;
  City?: string;
  ZipCode?: string;
  Country?: string;
}

/**
 * Populate form fields with ARES data
 */
function populateFormFields(data: AresLegalEntityInfo): void {
  // Helper to set field value and remove validation state
  const setFieldValue = (id: string, value: string | undefined): void => {
    const field = document.getElementById(id) as HTMLInputElement | null;
    if (field && value) {
      field.value = value;
      field.classList.remove('is-invalid');

      // Trigger input event for any listeners
      field.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  // Populate company fields
  setFieldValue('CompanyName', data.CompanyName);
  setFieldValue('CompanyTaxIdentificationNumber', data.CompanyTaxIdentificationNumber);

  // Populate delivery address fields
  setFieldValue('DeliveryStreet', data.Street);
  setFieldValue('DeliveryCity', data.City);
  setFieldValue('DeliveryZipCode', data.ZipCode);
  setFieldValue('DeliveryCountry', data.Country);

  // Populate invoice address fields (same as delivery from ARES)
  setFieldValue('InvoiceStreet', data.Street);
  setFieldValue('InvoiceCity', data.City);
  setFieldValue('InvoiceZipCode', data.ZipCode);
  setFieldValue('InvoiceCountry', data.Country);
}

/**
 * Update button state based on ICO field value
 */
function updateButtonState(button: HTMLButtonElement, icoInput: HTMLInputElement): void {
  const ico = icoInput.value.trim();
  if (ico.length > 0) {
    button.removeAttribute('disabled');
  } else {
    button.setAttribute('disabled', 'disabled');
  }
}

/**
 * Handle ARES lookup button click
 */
async function handleAresLookup(event: Event): Promise<void> {
  event.preventDefault();

  const button = event.currentTarget as HTMLButtonElement;
  const apiUrl = button.getAttribute('data-ares-url');
  if (!apiUrl) {
    // eslint-disable-next-line no-console
    console.error('ARES API URL not found on button');
    return;
  }

  // Get IČO value from input
  const icoInput = document.getElementById(
    'CompanyIdentificationNumber'
  ) as HTMLInputElement | null;
  if (!icoInput) {
    // eslint-disable-next-line no-console
    console.error('IČO input field not found');
    return;
  }

  const ico = icoInput.value.trim();
  try {
    const response = await fetch(`${apiUrl}?ico=${ico}`, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.error || 'Chyba při načítání dat z ARES';
      showToast({
        severity: ToastSeverity.Error,
        message: errorMessage,
      });
      return;
    }

    const data: AresLegalEntityInfo = await response.json();

    // Populate form fields
    populateFormFields(data);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('ARES lookup error:', error);
    showToast({
      severity: ToastSeverity.Error,
      message: 'Chyba při komunikaci se serverem',
    });
  } finally {
    // Restore button state
    updateButtonState(button, icoInput);
  }
}

/**
 * Initialize ARES lookup functionality
 */
export async function initAres(parent: HTMLElement | Document): Promise<void> {
  const buttons = parent.querySelectorAll<HTMLButtonElement>('.js-ares-btn');
  const icoInput = document.getElementById(
    'CompanyIdentificationNumber'
  ) as HTMLInputElement | null;

  if (!icoInput) {
    return;
  }

  buttons.forEach((button) => {
    // Set initial button state
    updateButtonState(button, icoInput);

    // Add click handler
    button.addEventListener('click', handleAresLookup);

    // Update button state on ICO input change
    icoInput.addEventListener('input', () => updateButtonState(button, icoInput));
  });
}
